/**
 * AppPublic.jsx — SEC-SPLIT-01 Public Portal Build
 *
 * This is the public-only route tree for the Prashasta Public Portal.
 * It imports ONLY public-facing pages — no CRM, Finance, Admin, or
 * UEIM private pages are imported or bundled here.
 *
 * Build mode: REACT_APP_BUILD_MODE=PUBLIC
 * API target: REACT_APP_BACKEND_URL → Public BFF (port 8002 or portal subdomain)
 *
 * Public routes registered (17 total — matches G0.1.A of SEC-SPLIT-00 audit):
 *   /                        LandingPage / EventJourney
 *   /event-journey           EventJourney
 *   /login                   Login
 *   /forgot-password         ForgotPassword
 *   /reset-password          ResetPassword
 *   /auth/callback/google    GoogleAuthCallback
 *   /signin                  AuthRedirect (→ login)
 *   /auth                    AuthRedirect (→ login)
 *   /uat-login               UATRolePicker (UAT only)
 *   /verify-2fa              Verify2FA
 *   /public/inquire/:alias   PublicInquiry
 *   /build-my-event          BuildMyEvent (GQP + Luxury Concierge)
 *   /explore                 ExplorePage
 *   /explore/:slug           EventStoryPage
 *   /find-events-like-mine   FindEventsPage
 *   /sourcing/quote/:token   SupplierQuoteForm (token-gated)
 *   *                        AuthRedirect (404 fallback)
 *
 * Security properties:
 *   - No imports from CRM, UEIM, DCC, WVB, Finance, Admin modules
 *   - No ProtectedRoute or Layout wrapper (public portal has no authenticated shell)
 *   - REACT_APP_BUILD_MODE=PUBLIC → bundle analysis should show zero private imports
 */

import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandProvider } from "@/contexts/BrandContext";
import { Toaster } from "@/components/ui/sonner";

// ── Public pages only ─────────────────────────────────────────────────────────
import EventJourney from "@/pages/EventJourney";
import Login from "@/pages/Login";
import UATRolePicker from "@/pages/UATRolePicker";
import Verify2FA from "@/pages/Verify2FA";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import PublicInquiry from "@/pages/PublicInquiry";
import BuildMyEvent from "@/pages/BuildMyEvent";
import SmartStudio from "@/pages/SmartStudio";
import ExplorePage from "@/pages/ExplorePage";
import EventStoryPage from "@/pages/EventStoryPage";
import FindEventsPage from "@/pages/FindEventsPage";
import SupplierQuoteForm from "@/pages/SupplierQuoteForm";

// ── Auth redirect (routes to login for unrecognized paths) ───────────────────
function AuthRedirect() {
  const { uatConfig } = useAuth();
  const isUAT =
    uatConfig?.auth_mode === "UAT_ROLE_PICKER" ||
    process.env.REACT_APP_ENVIRONMENT === "UAT";
  return (
    <meta
      httpEquiv="refresh"
      content={`0;url=${isUAT ? "/uat-login" : "/login"}`}
    />
  );
}

export default function AppPublic() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandProvider>
          <Toaster />
          <Routes>
            {/* Landing / Journey */}
            <Route path="/" element={<EventJourney />} />
            <Route path="/event-journey" element={<EventJourney />} />

            {/* Authentication */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback/google" element={<GoogleAuthCallback />} />
            <Route path="/signin" element={<AuthRedirect />} />
            <Route path="/auth" element={<AuthRedirect />} />
            <Route path="/uat-login" element={<UATRolePicker />} />
            <Route path="/verify-2fa" element={<Verify2FA />} />

            {/* Public guest journeys */}
            <Route path="/public/inquire/:alias" element={<PublicInquiry />} />
            <Route path="/build-my-event" element={<BuildMyEvent />} />
            <Route path="/smart-studio" element={<SmartStudio />} />
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/explore/:slug" element={<EventStoryPage />} />
            <Route path="/find-events-like-mine" element={<FindEventsPage />} />

            {/* Token-gated supplier portal */}
            <Route path="/sourcing/quote/:token" element={<SupplierQuoteForm />} />

            {/* 404 fallback */}
            <Route path="*" element={<AuthRedirect />} />
          </Routes>
        </BrandProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
